import { useState } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { LOGO_URL } from "../lib/api";

const FLOATING_VIALS = [
    "/products/glutathione.png",
    "/products/nad.png",
    "/products/ghkcu.png",
    "/products/motsc.png",
    "/products/ap3-30mg.png",
    "/products/ap3-10mg.png",
    "/products/tb500-vial.png",
    "/products/tesamorelin.png",
    "/products/selank.png",
    "/products/amino-h2o.png",
];

// Static positions for the floating product vials — deterministic so they don't jump on re-render
const POSITIONS = [
    { top: "6%",  left: "4%",  w: 180, rot: -18, blur: 0,    op: 0.55 },
    { top: "12%", left: "82%", w: 150, rot: 14,  blur: 1,    op: 0.45 },
    { top: "2%",  left: "55%", w: 110, rot: 30,  blur: 2,    op: 0.35 },
    { top: "38%", left: "2%",  w: 220, rot: -8,  blur: 0,    op: 0.6  },
    { top: "45%", left: "90%", w: 170, rot: 22,  blur: 1,    op: 0.5  },
    { top: "70%", left: "10%", w: 130, rot: -25, blur: 1.5,  op: 0.4  },
    { top: "75%", left: "78%", w: 200, rot: 10,  blur: 0,    op: 0.55 },
    { top: "88%", left: "40%", w: 120, rot: -12, blur: 2,    op: 0.32 },
    { top: "30%", left: "70%", w: 100, rot: 18,  blur: 3,    op: 0.25 },
    { top: "60%", left: "48%", w: 90,  rot: -30, blur: 3,    op: 0.22 },
];

const KEY = "aminoplex_age_verified_v1";

export function getAgeVerified() {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
}

export default function AgeGate({ onPass }) {
    const [age, setAge] = useState(false);
    const [researcher, setResearcher] = useState(false);

    const ready = age && researcher;

    const enter = () => {
        if (!ready) return;
        localStorage.setItem(KEY, "1");
        onPass();
    };

    return (
        <div data-testid="age-gate" className="fixed inset-0 z-[100] bg-[var(--bg)] overflow-y-auto">
            {/* Floating vials */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {FLOATING_VIALS.map((src, i) => {
                    const pos = POSITIONS[i % POSITIONS.length];
                    return (
                        <img
                            key={i}
                            src={src}
                            alt=""
                            style={{
                                position: "absolute",
                                top: pos.top,
                                left: pos.left,
                                width: pos.w,
                                opacity: pos.op,
                                filter: pos.blur ? `blur(${pos.blur}px)` : "none",
                                transform: `rotate(${pos.rot}deg)`,
                                transition: "transform 8s ease-in-out",
                            }}
                            className="hidden md:block"
                        />
                    );
                })}
            </div>

            {/* Grid + vignette */}
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="absolute inset-0 vignette pointer-events-none" />

            {/* Card */}
            <div className="relative min-h-screen flex flex-col items-center justify-center px-6 py-16">
                <img
                    src={LOGO_URL}
                    alt="Aminoplex"
                    className="h-24 w-24 mb-3 object-contain"
                    style={{ filter: "drop-shadow(0 0 40px rgba(201,169,97,0.35))" }}
                />
                <div className="font-display text-2xl tracking-[0.32em] text-brand mb-1">AMINOPLEX</div>
                <div className="hairline-gold w-24 mb-10" />

                <div className="w-full max-w-xl card p-8 md:p-10 backdrop-blur-xl bg-[rgba(18,18,18,0.85)]">
                    <div className="flex items-center gap-3 mb-2">
                        <ShieldCheck size={18} className="text-brand" />
                        <div className="data-label text-brand">RESEARCHER VERIFICATION</div>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl text-primary leading-tight mb-4" style={{ fontWeight: 700 }}>
                        Researcher verification
                    </h1>
                    <p className="text-secondary text-sm leading-relaxed mb-8">
                        Aminoplex sells research peptides exclusively to qualified researchers and laboratories for
                        in vitro and laboratory use. Please confirm before continuing.
                    </p>

                    <label className="flex items-start gap-3 p-4 border border-[var(--border)] hover:border-[var(--border-light)] cursor-pointer transition-colors mb-3" data-testid="age-checkbox-label">
                        <input
                            type="checkbox"
                            data-testid="age-checkbox"
                            checked={age}
                            onChange={(e) => setAge(e.target.checked)}
                            className="mt-1 accent-[var(--brand)] w-4 h-4 cursor-pointer"
                        />
                        <span className="text-sm text-primary leading-relaxed">
                            I am at least <span className="font-semibold text-brand">21 years of age</span>.
                        </span>
                    </label>

                    <label className="flex items-start gap-3 p-4 border border-[var(--border)] hover:border-[var(--border-light)] cursor-pointer transition-colors mb-7" data-testid="researcher-checkbox-label">
                        <input
                            type="checkbox"
                            data-testid="researcher-checkbox"
                            checked={researcher}
                            onChange={(e) => setResearcher(e.target.checked)}
                            className="mt-1 accent-[var(--brand)] w-4 h-4 cursor-pointer"
                        />
                        <span className="text-sm text-primary leading-relaxed">
                            I confirm I am a <span className="font-semibold text-brand">qualified researcher</span> purchasing
                            for <span className="font-semibold text-brand">in vitro / laboratory research</span> only — not
                            for human or veterinary use.
                        </span>
                    </label>

                    <button
                        onClick={enter}
                        disabled={!ready}
                        data-testid="enter-aminoplex-btn"
                        className={`btn w-full justify-center ${ready ? "btn-solid" : ""}`}
                        style={{ padding: "1rem 1.6rem" }}
                    >
                        Enter Aminoplex <ArrowRight size={14} />
                    </button>

                    <p className="text-[0.7rem] text-muted leading-relaxed mt-6">
                        By proceeding you affirm the statements above are true. Products are not for human or
                        veterinary use, not for use in diagnostic procedures, and have not been evaluated by
                        the U.S. Food and Drug Administration.
                    </p>
                </div>

                <p className="mt-8 text-xs text-muted">
                    Not a researcher?{" "}
                    <a href="https://www.google.com/" className="link-gold" data-testid="exit-link">Exit</a>
                </p>
            </div>
        </div>
    );
}
